import { useState, useEffect } from "react";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Snackbar from "@mui/material/Snackbar";
import { sendUserInvite, resendInvite, resetUserPassword, getUsers, updateUser, deleteUser } from "services/userApi";


const rolePermissions = {
  "Admin": {
    description: "Full system access and user management",
    permissions: ["full_access"]
  },
  "Property Manager": {
    description: "Property and tenant management",
    permissions: ["property_management", "tenant_management", "basic_reports"]
  },
  "Maintenance Coordinator": {
    description: "Maintenance request and vendor management",
    permissions: ["maintenance_management", "vendor_management"]
  },
  "Financial Analyst": {
    description: "Financial reports and billing access",
    permissions: ["financial_reports", "billing_management"]
  },
  "Viewer": {
    description: "Read-only access to assigned properties",
    permissions: ["view_only"]
  }
};

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [addUserDialog, setAddUserDialog] = useState(false);
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuUserId, setMenuUserId] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: "", severity: "success" });
  
  // Add user form state
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    role: "Viewer",
    sendInvite: true
  });
  
  // Edit user form state
  const [editUser, setEditUser] = useState({
    name: "",
    email: "",
    role: "",
    status: "Active"
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      showNotification("Failed to load users", "error");
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, severity = "success") => {
    setNotification({ open: true, message, severity });
  };

  const handleMenuClick = (event, userId) => {
    setAnchorEl(event.currentTarget);
    setMenuUserId(userId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUserId(null);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditUser({
      name: user.name,
      email: user.email,
      role: user.role,
      status: user.status
    });
    setEditUserDialog(true);
    handleMenuClose();
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteUser(userId);
      setUsers(users.filter(user => user.id !== userId));
      showNotification("User deleted successfully");
    } catch (error) {
      console.error("Failed to delete user:", error);
      showNotification("Failed to delete user", "error");
    }
    handleMenuClose();
  };

  const handleResetPassword = async (userId) => {
    try {
      const user = users.find(u => u.id === userId);
      await resetUserPassword(userId);
      showNotification(`Password reset email sent to ${user?.email}`);
    } catch (error) {
      console.error("Failed to reset password:", error);
      showNotification("Failed to send password reset email", "error");
    }
    handleMenuClose();
  };

  const handleResendInvite = async (userId) => {
    try {
      const user = users.find(u => u.id === userId);
      if (user && user.status === "Pending") {
        await resendInvite(userId);
        showNotification(`Invitation resent to ${user.email}`);
      }
    } catch (error) {
      console.error("Failed to resend invite:", error);
      showNotification("Failed to resend invitation", "error");
    }
    handleMenuClose();
  };

  const handleAddUser = async () => {
    try {
      if (newUser.sendInvite) {
        // Send invite email through API
        const inviteData = {
          email: newUser.email,
          role: newUser.role,
          name: newUser.name
        };
        
        const response = await sendUserInvite(inviteData);
        
        // Add user with pending status
        const user = {
          id: response.id || Date.now(),
          name: newUser.name,
          email: newUser.email,
          role: newUser.role,
          status: "Pending",
          lastLogin: "Never",
          avatar: "team-1.jpg",
          permissions: rolePermissions[newUser.role]?.permissions || [],
          inviteSentAt: new Date().toISOString()
        };
        
        setUsers([...users, user]);
        showNotification("Invitation sent successfully");
      } else {
        // Direct user creation (would require password)
        showNotification("Direct user creation not implemented yet", "warning");
        return;
      }
      
      setNewUser({ name: "", email: "", role: "Viewer", sendInvite: true });
      setAddUserDialog(false);
    } catch (error) {
      console.error("Failed to add user:", error);
      showNotification("Failed to send invitation", "error");
    }
  };

  const handleUpdateUser = async () => {
    try {
      await updateUser(selectedUser.id, editUser);
      setUsers(users.map(user => 
        user.id === selectedUser.id 
          ? { ...user, ...editUser }
          : user
      ));
      setEditUserDialog(false);
      setSelectedUser(null);
      showNotification("User updated successfully");
    } catch (error) {
      console.error("Failed to update user:", error);
      showNotification("Failed to update user", "error");
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "Active": return "success";
      case "Inactive": return "error";
      case "Pending": return "warning";
      default: return "default";
    }
  };

  const getUserStats = () => {
    const total = users.length;
    const active = users.filter(u => u.status === "Active").length;
    const pending = users.filter(u => u.status === "Pending").length;
    const inactive = users.filter(u => u.status === "Inactive").length;
    
    return { total, active, pending, inactive };
  };

  const stats = getUserStats();

  return (
    <ArgonBox>
      
      {/* User Statistics */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={6} md={3}>
          <Card>
            <ArgonBox p={2} textAlign="center">
              <ArgonTypography variant="h3" fontWeight="bold" color="primary">
                {stats.total}
              </ArgonTypography>
              <ArgonTypography variant="body2" color="text">
                Total Users
              </ArgonTypography>
            </ArgonBox>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <ArgonBox p={2} textAlign="center">
              <ArgonTypography variant="h3" fontWeight="bold" color="success">
                {stats.active}
              </ArgonTypography>
              <ArgonTypography variant="body2" color="text">
                Active Users
              </ArgonTypography>
            </ArgonBox>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <ArgonBox p={2} textAlign="center">
              <ArgonTypography variant="h3" fontWeight="bold" color="warning">
                {stats.pending}
              </ArgonTypography>
              <ArgonTypography variant="body2" color="text">
                Pending Invites
              </ArgonTypography>
            </ArgonBox>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <ArgonBox p={2} textAlign="center">
              <ArgonTypography variant="h3" fontWeight="bold" color="error">
                {stats.inactive}
              </ArgonTypography>
              <ArgonTypography variant="body2" color="text">
                Inactive Users
              </ArgonTypography>
            </ArgonBox>
          </Card>
        </Grid>
      </Grid>

      {/* Actions and Search */}
      <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <ArgonBox flex={1} mr={2}>
          <ArgonInput
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            inputProps={{
              startAdornment: <Icon sx={{ mr: 1 }}>search</Icon>
            }}
          />
        </ArgonBox>
        <ArgonBox display="flex" gap={1}>
          <ArgonButton 
            color="info" 
            variant="outlined" 
            size="small"
            startIcon={<Icon>file_download</Icon>}
          >
            Export
          </ArgonButton>
          <ArgonButton 
            color="primary" 
            size="small"
            startIcon={<Icon>person_add</Icon>}
            onClick={() => setAddUserDialog(true)}
          >
            Add User
          </ArgonButton>
        </ArgonBox>
      </ArgonBox>

      {/* Users Table */}
      <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell>
                  <ArgonBox display="flex" alignItems="center">
                    <Avatar 
                      src={`/static/images/${user.avatar}`}
                      sx={{ mr: 2, width: 40, height: 40 }}
                    >
                      {user.name.charAt(0)}
                    </Avatar>
                    <ArgonBox>
                      <ArgonTypography variant="body2" fontWeight="medium">
                        {user.name}
                      </ArgonTypography>
                      <ArgonTypography variant="caption" color="text">
                        {user.email}
                      </ArgonTypography>
                    </ArgonBox>
                  </ArgonBox>
                </TableCell>
                <TableCell>
                  <ArgonTypography variant="body2">
                    {user.role}
                  </ArgonTypography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={user.status}
                    color={getStatusColor(user.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <ArgonTypography variant="caption" color="text">
                    {user.lastLogin}
                    {user.status === "Pending" && user.inviteSentAt && (
                      <ArgonTypography variant="caption" color="text" display="block">
                        Invite sent {new Date(user.inviteSentAt).toLocaleDateString()}
                      </ArgonTypography>
                    )}
                  </ArgonTypography>
                </TableCell>
                <TableCell align="center">
                  <IconButton 
                    size="small"
                    onClick={(e) => handleMenuClick(e, user.id)}
                  >
                    <Icon>more_vert</Icon>
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditUser(users.find(u => u.id === menuUserId))}>
          <Icon fontSize="small" sx={{ mr: 1 }}>edit</Icon>
          Edit User
        </MenuItem>
        <MenuItem onClick={() => handleResetPassword(menuUserId)}>
          <Icon fontSize="small" sx={{ mr: 1 }}>lock_reset</Icon>
          Reset Password
        </MenuItem>
        <MenuItem onClick={() => handleResendInvite(menuUserId)}>
          <Icon fontSize="small" sx={{ mr: 1 }}>email</Icon>
          Resend Invite
        </MenuItem>
        <MenuItem onClick={() => console.log("View permissions")}>
          <Icon fontSize="small" sx={{ mr: 1 }}>security</Icon>
          Permissions
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => handleDeleteUser(menuUserId)}
          sx={{ color: "error.main" }}
        >
          <Icon fontSize="small" sx={{ mr: 1 }}>delete</Icon>
          Delete User
        </MenuItem>
      </Menu>

      {/* Add User Dialog */}
      <Dialog open={addUserDialog} onClose={() => setAddUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <ArgonTypography variant="h5" fontWeight="medium">
            Add New User
          </ArgonTypography>
        </DialogTitle>
        <DialogContent>
          <ArgonBox mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={newUser.role}
                    label="Role"
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  >
                    {Object.keys(rolePermissions).map(role => (
                      <MenuItem key={role} value={role}>
                        <ArgonBox>
                          <ArgonTypography variant="body2" fontWeight="medium">
                            {role}
                          </ArgonTypography>
                          <ArgonTypography variant="caption" color="text">
                            {rolePermissions[role].description}
                          </ArgonTypography>
                        </ArgonBox>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newUser.sendInvite}
                      onChange={(e) => setNewUser({...newUser, sendInvite: e.target.checked})}
                    />
                  }
                  label="Send invitation email"
                />
                {newUser.sendInvite && (
                  <ArgonTypography variant="caption" color="text" display="block" ml={3}>
                    User will receive an email with a link to set up their account
                  </ArgonTypography>
                )}
              </Grid>
            </Grid>
            
            {newUser.role && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <ArgonTypography variant="body2" fontWeight="medium" mb={1}>
                  {newUser.role} Permissions:
                </ArgonTypography>
                <ArgonTypography variant="caption">
                  {rolePermissions[newUser.role]?.description}
                </ArgonTypography>
              </Alert>
            )}
          </ArgonBox>
        </DialogContent>
        <DialogActions>
          <ArgonButton color="secondary" onClick={() => setAddUserDialog(false)}>
            Cancel
          </ArgonButton>
          <ArgonButton 
            color="primary" 
            onClick={handleAddUser}
            disabled={!newUser.name || !newUser.email}
          >
            Add User
          </ArgonButton>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialog} onClose={() => setEditUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <ArgonTypography variant="h5" fontWeight="medium">
            Edit User
          </ArgonTypography>
        </DialogTitle>
        <DialogContent>
          <ArgonBox mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={editUser.name}
                  onChange={(e) => setEditUser({...editUser, name: e.target.value})}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={editUser.email}
                  onChange={(e) => setEditUser({...editUser, email: e.target.value})}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={editUser.role}
                    label="Role"
                    onChange={(e) => setEditUser({...editUser, role: e.target.value})}
                  >
                    {Object.keys(rolePermissions).map(role => (
                      <MenuItem key={role} value={role}>{role}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editUser.status}
                    label="Status"
                    onChange={(e) => setEditUser({...editUser, status: e.target.value})}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </ArgonBox>
        </DialogContent>
        <DialogActions>
          <ArgonButton color="secondary" onClick={() => setEditUserDialog(false)}>
            Cancel
          </ArgonButton>
          <ArgonButton color="primary" onClick={handleUpdateUser}>
            Update User
          </ArgonButton>
        </DialogActions>
      </Dialog>
      
      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          severity={notification.severity} 
          onClose={() => setNotification({ ...notification, open: false })}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </ArgonBox>
  );
}

export default UserManagement;